import React, { useState, useCallback, useEffect, ChangeEvent } from 'react'
import { NotifiableError } from '@bugsnag/js'
import { useRouter } from 'next/router'
import Loader from 'react-loader-spinner'
import { Button, ButtonBase, Text, ErrorBox } from '@/components/common'
import { emailLogin } from '@/services/api/auth'
import useAuth from '@/services/hooks/useAuth'
import { resolveErrorMessage, report } from '@/utils'
import tailwind from '../../../tailwind.config'
import AuthInput from './AuthInput'
import FacebookAuthButton from './FacebookAuthButton'
import GoogleAuthButton from './GoogleAuthButton'

export interface Props {
  email: string
  setEmail: (email: string) => void
  setView: (view: string) => void
}

const LoginForm: React.FC<Props> = ({ email, setEmail, setView }) => {
  const [error, setError] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const { persistUser, closeModal, redirect } = useAuth()
  const [enableGoogleLogin, setEnableGoogleLogin] = useState(false)
  const router = useRouter()

  useEffect(() => {
    const { disable_google_login } = router.query
    // google login does not work inside Facebook's in-app browser
    const isFacebookInAppBrowser =
      navigator.userAgent.indexOf('FBAN') > -1 ||
      navigator.userAgent.indexOf('FBAV') > -1

    setEnableGoogleLogin(
      disable_google_login !== 'true' && !isFacebookInAppBrowser
    )
  }, [router.query])

  const socialPrompt = enableGoogleLogin
    ? 'If you have previously logged in with Google or Facebook, please login below.'
    : 'If you have previously logged in with Facebook, please login below.'

  const handleLogin = useCallback(
    async (e) => {
      e.preventDefault()

      try {
        setLoading(true)
        const res = await emailLogin(password, email)
        persistUser(res.uuid, res.authorization, null, redirect)
        closeModal()
      } catch (err) {
        report.log(err as NotifiableError)
        setError(resolveErrorMessage(err as Error))
      } finally {
        setLoading(false)
      }
    },
    [persistUser, closeModal, redirect, email, password]
  )

  return (
    <>
      <form
        className="flex w-full flex-col items-center"
        onSubmit={handleLogin}
      >
        <ErrorBox errorText={error} className="mb-4" />

        <AuthInput
          name="email"
          type="email"
          placeholder="EMAIL"
          value={email}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setEmail(e.target.value)
          }
        />
        <AuthInput
          name="password"
          type="password"
          placeholder="PASSWORD"
          className="mt-4"
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setPassword(e.target.value)
          }
        />
        <Button
          className="mb-2 mt-4 flex w-full items-center justify-center rounded-full"
          type="submit"
          disabled={loading || !email || !password}
        >
          {loading && (
            <div className="mr-2">
              <Loader
                type="TailSpin"
                color={tailwind.theme.colors.lightGray}
                height={25}
                width={25}
              />
            </div>
          )}
          <span>Login</span>
        </Button>

        <ButtonBase
          className="text-sm text-core-oxide"
          onClick={() => setView('resetPassword')}
          disabled={loading}
          type="button"
        >
          Forgot your password?
        </ButtonBase>
      </form>

      <div className="mt-6 flex w-full flex-col items-center">
        <Text as="p" preset="body.sm" className="text-center text-gray-4">
          {socialPrompt}
        </Text>

        <FacebookAuthButton setError={setError} className="mt-3 w-full" />
        {enableGoogleLogin && (
          <GoogleAuthButton setError={setError} className="mt-3 w-full" />
        )}
      </div>
    </>
  )
}

export default LoginForm
